import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TrendingUp, Plus, RefreshCw } from "lucide-react";
import { canAccessModule } from "@/utils/permissions";
import "./cabina.css";

const fmt4 = (v) => (v == null ? "—" : Number(v).toFixed(4));
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function AdminMonedasTC() {
  const { user } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [tab, setTab] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState(daysAgo(30));
  const [hasta, setHasta] = useState(todayISO());
  const [showCapture, setShowCapture] = useState(false);
  const canView = canAccessModule(user, "admin_monedas_tc");

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/monedas/config`);
      const active = (r.data.currencies || []).filter((c) => c.is_active && c.code !== "MXN");
      setCurrencies(active);
      if (!tab && active[0]) setTab(active[0].code);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  }, [tab]);

  const loadHistory = useCallback(async () => {
    if (!tab) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/monedas/tc/history`, { params: { currency_code: tab, desde, hasta } });
      setRows(r.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setLoading(false);
  }, [tab, desde, hasta]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const refreshBanxico = async () => {
    try {
      await axios.post(`${API}/monedas/tc/refresh-banxico`);
      toast.success("Banxico actualizado");
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!canView) {
    return (
      <div className="cv" style={{ padding: 22 }}>
        <div className="rulebox" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
          <b>Sin acceso.</b> No tienes permiso para consultar los tipos de cambio históricos.
        </div>
      </div>
    );
  }

  const curActual = currencies.find((c) => c.code === tab);
  const esBanxico = !!curActual?.banxico_series;

  return (
    <div className="cv" style={{ padding: 22 }} data-testid="admin-monedas-tc">
      <div className="head">
        <div>
          <h2><TrendingUp size={16} style={{ verticalAlign: -2, marginRight: 6 }} />Tipos de Cambio</h2>
          <p>Historial diario del TC oficial de BANXICO y el TC PD TRAVEL (BANXICO + incremento) por moneda activa.</p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="btn ghost sm" onClick={refreshBanxico} data-testid="btn-refresh-banxico">
            <RefreshCw size={12} /> Actualizar Banxico
          </button>
          {tab && !esBanxico && (
            <button className="btn dark sm" onClick={() => setShowCapture(true)} data-testid="btn-capture-tc">
              <Plus size={12} /> Capturar TC {tab}
            </button>
          )}
        </div>
      </div>

      {/* Tabs de monedas activas */}
      {currencies.length === 0 ? (
        <div className="rulebox" style={{ marginTop: 16 }}>
          No hay monedas activas (aparte de MXN). Actívalas en <b>Configuración</b>.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--cv-line)", marginTop: 16, overflowX: "auto" }}>
            {currencies.map((c) => (
              <button key={c.code}
                onClick={() => setTab(c.code)}
                data-testid={`tab-${c.code}`}
                style={{
                  background: "none", border: 0, padding: "10px 18px", cursor: "pointer",
                  fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
                  borderBottom: tab === c.code ? "2px solid var(--cv-blue)" : "2px solid transparent",
                  color: tab === c.code ? "var(--cv-blue)" : "var(--cv-muted)",
                }}>
                {c.symbol} {c.code}
                <span style={{ color: "var(--cv-muted)", fontSize: 11, fontWeight: 500, marginLeft: 6 }}>
                  · inc {(c.incremento ?? 0).toFixed(2)}
                </span>
              </button>
            ))}
          </div>

          {/* Filtro de fecha */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginTop: 14 }}>
            <label className="f"><span>Desde</span><input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="tc-desde" /></label>
            <label className="f"><span>Hasta</span><input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="tc-hasta" /></label>
            <button className="btn sm" onClick={loadHistory}>Consultar</button>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <div className="tw">
              {loading ? <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Cargando…</p>
                : rows.length === 0 ? <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Sin registros para este rango.</p>
                : (
                  <table>
                    <thead>
                      <tr>
                        <th>Fecha</th><th>Moneda</th>
                        <th className="r">TC BANXICO Inicio</th>
                        <th className="r">TC BANXICO 12pm</th>
                        <th className="r">TC PD TRAVEL Inicio</th>
                        <th className="r">TC PD TRAVEL 12pm</th>
                        <th className="r">Incremento</th>
                        <th style={{ textAlign: "center" }}>Fuente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={`${r.currency_code}-${r.fecha}`} data-testid={`row-${r.currency_code}-${r.fecha}`}>
                          <td className="mono"><b>{r.fecha}</b></td>
                          <td><b style={{ color: "var(--cv-blue)" }}>{r.currency_code}</b></td>
                          <td className="r mono">{fmt4(r.tc_banxico_inicio)}</td>
                          <td className="r mono">{fmt4(r.tc_banxico_1200)}</td>
                          <td className="r mono"><b>{fmt4(r.tc_pdtravel_inicio)}</b></td>
                          <td className="r mono"><b>{fmt4(r.tc_pdtravel_1200)}</b></td>
                          <td className="r mono">{Number(r.incremento_aplicado || 0).toFixed(4)}</td>
                          <td style={{ textAlign: "center", fontSize: 11.5 }}>
                            <span className={`chip ${r.source === "banxico" ? "c-val" : "c-warn"}`}>{r.source || "—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </>
      )}

      {showCapture && curActual && (
        <CaptureTCDialog moneda={curActual} onClose={() => setShowCapture(false)} onSaved={() => { setShowCapture(false); loadHistory(); }} />
      )}
    </div>
  );
}

function CaptureTCDialog({ moneda, onClose, onSaved }) {
  const [form, setForm] = useState({
    fecha: todayISO(),
    tc_banxico_inicio: "",
    tc_banxico_1200: "",
  });
  const submit = async () => {
    const body = {
      currency_code: moneda.code,
      fecha: form.fecha,
    };
    if (form.tc_banxico_inicio !== "") body.tc_banxico_inicio = Number(form.tc_banxico_inicio);
    if (form.tc_banxico_1200 !== "") body.tc_banxico_1200 = Number(form.tc_banxico_1200);
    if (body.tc_banxico_inicio === undefined && body.tc_banxico_1200 === undefined) {
      toast.error("Captura al menos un valor (inicio o 12pm)"); return;
    }
    try {
      await axios.post(`${API}/monedas/tc/manual`, body);
      toast.success(`${moneda.code} guardado`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 520, padding: 0 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Capturar TC {moneda.code}</h3>
          <p style={{ margin: "4px 0 0", color: "var(--cv-muted)", fontSize: 12.5 }}>Incremento vigente: <b>{(moneda.incremento ?? 0).toFixed(4)}</b>. Sólo se permiten fechas de hoy o futuras.</p>
        </div>
        <div style={{ padding: 20 }}>
          <div className="grid g2">
            <label className="f"><span>Fecha</span><input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} min={todayISO()} data-testid="cap-fecha" /></label>
            <div />
            <label className="f"><span>TC BANXICO Inicio</span><input type="number" step="0.0001" value={form.tc_banxico_inicio} onChange={(e) => setForm({ ...form, tc_banxico_inicio: e.target.value })} data-testid="cap-inicio" /></label>
            <label className="f"><span>TC BANXICO 12pm</span><input type="number" step="0.0001" value={form.tc_banxico_1200} onChange={(e) => setForm({ ...form, tc_banxico_1200: e.target.value })} data-testid="cap-1200" /></label>
          </div>
          <p style={{ fontSize: 12, color: "var(--cv-muted)", marginTop: 12 }}>
            El TC PD TRAVEL se calculará automáticamente: TC BANXICO + {(moneda.incremento ?? 0).toFixed(4)}.
          </p>
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="cap-guardar">Guardar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
